// cdpDumpSegmentCompatible.js
//
// Plain JavaScript plugin for DavidWells/analytics.
// Goal: mimic Segment semantics as closely as practical and send Segment-shaped events
// to your cdpDump ingest endpoint via /{endpointPrefix}/batch.
//
// Usage (webpack/CRA):
//   import Analytics from 'analytics'
//   import cdpDumpPlugin from './cdpDumpSegmentCompatible'
//
//   const a = Analytics({
//     app: 'plickers-web',
//     plugins: [cdpDumpPlugin({
//       writeKey: '...',
//       host: 'https://analytics.yourdomain.com',
//       endpointPrefix: '/v1',
//     })]
//   })
//
// Notes:
// - Emits Segment-like message structure:
//   { type, messageId, timestamp, sentAt, anonymousId, userId, context, integrations,
//     properties/traits, ... }
// - Stores anonymousId in cookie "ajs_anonymous_id" (JSON-stringified string, Segment-ish).
// - Stores userId in localStorage "ajs_user_id" (Segment-ish).
// - Auth: writeKey is included in each message body (no Authorization header).
// - sentAt is set at send time, not enqueue time.
//
// Transport modes:
// - immediate: send on every event (still respects size limits and retries).
// - batch: only send on flushAt/interval/explicit flush.
// - hybrid (default): identify/alias/group send immediately; track/page micro-batch.
//
// Queue + durability:
// - In-memory queue mirrored to IndexedDB "cdpDumpPluginQueue" (store: "events").
// - Events are removed only after a successful send; retry on network/408/429/5xx.
// - Retry uses exponential backoff with jitter; drops after retryMax attempts.
//
// Queue limits:
// - maxEventBytes drops oversized events immediately.
// - maxBatchBytes caps fetch batches; maxBeaconBytes caps unload/beacon batches.
// - maxQueueLength drops oldest/newest (dropPolicy) when full.

export default function cdpDumpPlugin(userCfg = {}) {
  const cfg = {
    writeKey: null, // required
    host: null, // required
    endpointPrefix: '/v1',
    flushAt: 20,
    flushIntervalMs: 5000,
    sendBeacon: true, // safe for body auth; used on exit flush
    mode: 'hybrid', // immediate | batch | hybrid
    microBatchMs: 500,
    maxBatchBytes: 64 * 1024,
    maxBeaconBytes: 24 * 1024,
    maxEventBytes: 32 * 1024,
    maxQueueLength: 1000,
    retryMax: 5,
    retryBaseMs: 500,
    retryMaxMs: 30000,
    dropPolicy: 'oldest', // oldest | newest
    appName: null,
    cookieNameAnonymousId: 'ajs_anonymous_id',
    cookieDays: 365,
    storageKeyUserId: 'ajs_user_id',
    storageKeyGroupId: 'ajs_group_id',
    enableDebugLogging: false,
    ...(userCfg || {}),
  }

  if (!cfg.writeKey) throw new Error('[cdpDumpPlugin] writeKey is required')
  if (!cfg.host) throw new Error('[cdpDumpPlugin] host is required')

  let appName = 'app'
  let sequence = 0
  let queue = []
  const inFlight = new Set()
  let intervalHandle = null
  let microBatchHandle = null
  let isFlushing = false
  let unloadHandled = false
  let dbPromise = null
  let dbAvailable = true

  function debug(...args) {
    if (!cfg.enableDebugLogging) return
    // eslint-disable-next-line no-console
    console.log('[cdpDumpPlugin]', ...args)
  }

  function isoNow() {
    return new Date().toISOString()
  }

  function uuidv4() {
    if (window.crypto && window.crypto.randomUUID) {
      return window.crypto.randomUUID()
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.floor(Math.random() * 16)
      const v = c === 'x' ? r : (r % 4) + 8
      return v.toString(16)
    })
  }

  function buildBatchUrl() {
    const host = String(cfg.host || '').replace(/\/+$/, '')
    const prefix = String(cfg.endpointPrefix || '').replace(/^\/?/, '/').replace(/\/+$/, '')
    return `${host}${prefix}/batch`
  }

  function safeJsonParse(s) {
    if (!s) return null
    try {
      return JSON.parse(s)
    } catch (_) {
      return null
    }
  }

  function readCookie(name) {
    const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1')
    const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`))
    return match ? decodeURIComponent(match[1]) : null
  }

  function writeCookie(name, value, days) {
    const d = new Date()
    d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000)
    const expires = `; expires=${d.toUTCString()}`
    document.cookie =
      `${name}=${encodeURIComponent(value)}${expires}; path=/; SameSite=Lax`
  }

  function removeCookie(name) {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`
  }

  function getLocalStorage(key) {
    try {
      return window.localStorage.getItem(key)
    } catch (_) {
      return null
    }
  }

  function setLocalStorage(key, val) {
    try {
      if (val === null || val === undefined) window.localStorage.removeItem(key)
      else window.localStorage.setItem(key, String(val))
    } catch (_) {
      // ignore
    }
  }

  function normalizeAnonId(rawCookieValue) {
    if (!rawCookieValue) return null
    const parsed = safeJsonParse(rawCookieValue)
    if (typeof parsed === 'string') return parsed
    return rawCookieValue
  }

  function ensureAnonymousId() {
    const existing = normalizeAnonId(readCookie(cfg.cookieNameAnonymousId))
    if (existing) return existing
    const anon = uuidv4()
    // Segment-ish cookie style: JSON-stringified string
    writeCookie(cfg.cookieNameAnonymousId, JSON.stringify(anon), cfg.cookieDays)
    return anon
  }

  function getUserId() {
    return getLocalStorage(cfg.storageKeyUserId)
  }

  function setUserId(userId) {
    setLocalStorage(cfg.storageKeyUserId, userId)
  }

  function getGroupId() {
    return getLocalStorage(cfg.storageKeyGroupId)
  }

  function setGroupId(groupId) {
    setLocalStorage(cfg.storageKeyGroupId, groupId)
  }

  function nowMs() {
    return Date.now()
  }

  function estimateBytes(value) {
    try {
      const json = JSON.stringify(value)
      if (window.Blob) {
        return new Blob([json], { type: 'application/json' }).size
      }
      return json.length
    } catch (e) {
      return 0
    }
  }

  function getDb() {
    if (!dbAvailable || !window.indexedDB) {
      dbAvailable = false
      return Promise.resolve(null)
    }
    if (dbPromise) return dbPromise
    dbPromise = new Promise((resolve) => {
      const request = window.indexedDB.open('cdpDumpPluginQueue', 1)
      request.onupgradeneeded = (event) => {
        const db = event.target.result
        if (!db.objectStoreNames.contains('events')) {
          db.createObjectStore('events', { keyPath: 'id' })
        }
      }
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => {
        dbAvailable = false
        resolve(null)
      }
    })
    return dbPromise
  }

  async function persistItem(item) {
    const db = await getDb()
    if (!db) return
    await new Promise((resolve) => {
      try {
        const tx = db.transaction('events', 'readwrite')
        tx.objectStore('events').put(item)
        tx.oncomplete = () => {
          resolve()
        }
        tx.onerror = (e) => {
          // for transaction/request errors
          console.error('cdpDump - failed to persist item (onerror)', e)
          resolve()
        }
      } catch (e) {
        // for immediate errors (e.g. DataCloneError)
        console.error('cdpDump - failed to persist item (catch)', e)
        resolve()
      }
    })
  }

  async function removePersisted(id) {
    const db = await getDb()
    if (!db) return
    await new Promise((resolve) => {
      const tx = db.transaction('events', 'readwrite')
      tx.objectStore('events').delete(id)
      tx.oncomplete = () => resolve()
      tx.onerror = () => resolve()
    })
  }

  async function loadPersisted() {
    const db = await getDb()
    if (!db) return
    const stored = await new Promise((resolve) => {
      const tx = db.transaction('events', 'readonly')
      const store = tx.objectStore('events')
      if (store.getAll) {
        const request = store.getAll()
        request.onsuccess = () => resolve(request.result || [])
        request.onerror = () => resolve([])
      } else {
        const items = []
        store.openCursor().onsuccess = (event) => {
          const cursor = event.target.result
          if (cursor) {
            items.push(cursor.value)
            cursor.continue()
          } else {
            resolve(items)
          }
        }
      }
    })
    if (!stored.length) return
    const normalized = stored.map((item) => ({
      ...item,
      bytes: item.bytes || estimateBytes(item.message),
      createdAt: item.createdAt || nowMs(),
      nextRetryAt: item.nextRetryAt || 0,
      attempts: item.attempts || 0,
    }))
    normalized.sort((a, b) => a.createdAt - b.createdAt)
    queue = queue.concat(normalized)
    if (queue.length > cfg.maxQueueLength) {
      const overflow = queue.length - cfg.maxQueueLength
      const removed = queue.splice(0, overflow)
      removed.forEach((item) => {
        dropItem(item, 'queue_overflow')
      })
    }
  }

  function baseContext() {
    const nav = window.navigator || {}
    let tz = null
    try {
      tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    } catch (_) {
      // ignore missing Intl support
    }
    const { userAgentData: navUserAgentData } = nav
    let userAgentData
    if (navUserAgentData) {
      if (typeof navUserAgentData.toJSON === 'function') {
        userAgentData = navUserAgentData.toJSON()
      } else {
        userAgentData = navUserAgentData
      }
    }
    return {
      app: { name: appName },
      library: { name: 'cdpDump', version: '1' },
      page: {
        path: window.location.pathname,
        referrer: document.referrer || '',
        search: window.location.search || '',
        title: document.title || '',
        url: window.location.href,
      },
      userAgent: nav.userAgent,
      userAgentData,
      locale: nav.language || nav.userLanguage || null,
      timezone: tz,
      screen: {
        width: window.screen && window.screen.width,
        height: window.screen && window.screen.height,
        density: window.devicePixelRatio || null,
      },
    }
  }

  function makeBaseMessage(type) {
    const ts = isoNow()
    const msg = {
      type,
      messageId: `cdp-dump-${uuidv4()}`,
      sequence: sequence + 1,
      timestamp: ts,
      anonymousId: ensureAnonymousId(),
      userId: getUserId() || undefined,
      context: baseContext(),
      integrations: {},
      writeKey: cfg.writeKey,
      _metadata: {
        bundled: ['Segment.io'],
        unbundled: [],
        bundledIds: [],
      },
    }
    sequence += 1
    // optionally include groupId in context (Segment doesn't standardize this, but harmless)
    const gid = getGroupId()
    if (gid) msg.context.groupId = gid
    return msg
  }

  function dropItem(item, reason) {
    if (!item) return
    debug('drop event', reason, item.id)
    removePersisted(item.id)
  }

  function addItem(item) {
    queue.push(item)
    persistItem(item)
  }

  function enqueueMessage(msg) {
    const bytes = estimateBytes(msg)
    if (bytes > cfg.maxEventBytes) {
      debug('drop oversized event', bytes)
      return null
    }

    const item = {
      id: msg.messageId,
      message: msg,
      attempts: 0,
      nextRetryAt: 0,
      bytes,
      createdAt: nowMs(),
    }

    if (cfg.mode !== 'immediate' && queue.length > 0) {
      const currentSize = estimateBytes({ batch: queue.map((q) => q.message) })
      const nextSize = estimateBytes({ batch: queue.map((q) => q.message).concat([msg]) })
      if (nextSize > cfg.maxBatchBytes && currentSize > 0) {
        flush({ reason: 'maxBatchBytes' })
      }
    }

    if (queue.length >= cfg.maxQueueLength) {
      if (cfg.dropPolicy === 'newest') {
        dropItem(item, 'queue_full')
        return null
      }
      const dropped = queue.shift()
      if (dropped) dropItem(dropped, 'queue_full')
    }

    addItem(item)
    return item
  }

  function isIdentityType(type) {
    return type === 'identify' || type === 'alias' || type === 'group'
  }

  function scheduleMicroBatch() {
    if (microBatchHandle !== null) return
    microBatchHandle = window.setTimeout(() => {
      microBatchHandle = null
      flush({ reason: 'microBatch' })
    }, cfg.microBatchMs)
  }

  function scheduleSend(msg) {
    const item = enqueueMessage(msg)
    if (!item) return
    const immediate = cfg.mode === 'immediate' || (cfg.mode === 'hybrid' && isIdentityType(msg.type))
    if (immediate) {
      flush({ reason: 'immediate' })
      return
    }
    if (queue.length >= cfg.flushAt) {
      flush({ reason: 'flushAt' })
      return
    }
    if (cfg.mode === 'hybrid') {
      scheduleMicroBatch()
    }
  }

  function isRetryableStatus(status) {
    return status === 408 || status === 429 || (status >= 500 && status <= 599)
  }

  function computeBackoff(attempts) {
    const base = cfg.retryBaseMs * (2 ** attempts)
    const jitter = Math.random() * cfg.retryBaseMs
    return Math.min(cfg.retryMaxMs, base + jitter)
  }

  function takeBatch(maxBytes, { allowDropLarge = true, forBeacon = false } = {}) {
    const now = nowMs()
    const items = []
    const toDrop = new Set()
    let reachedLimit = false
    queue.forEach((item) => {
      if (reachedLimit) return
      if (item.nextRetryAt > now) return
      if (inFlight.has(item.id)) return
      const candidate = items.map((i) => i.message).concat([item.message])
      const size = estimateBytes({ batch: candidate })
      if (size > maxBytes) {
        if (items.length > 0) {
          reachedLimit = true
          return
        }
        if (forBeacon) return
        if (allowDropLarge) toDrop.add(item.id)
        return
      }
      items.push(item)
    })
    if (toDrop.size > 0) {
      queue = queue.filter((item) => {
        if (toDrop.has(item.id)) {
          dropItem(item, 'batch_too_large')
          return false
        }
        return true
      })
    }
    if (items.length === 0) return []
    const ids = new Set(items.map((i) => i.id))
    queue = queue.filter((item) => !ids.has(item.id))
    items.forEach((item) => inFlight.add(item.id))
    return items
  }

  function markSuccess(items) {
    items.forEach((item) => {
      inFlight.delete(item.id)
      removePersisted(item.id)
    })
  }

  function markDrop(items, status) {
    debug('drop after failure', status)
    items.forEach((item) => {
      inFlight.delete(item.id)
      dropItem(item, 'send_failed')
    })
  }

  function requeueItem(item, status) {
    const nextAttempts = item.attempts + 1
    if (nextAttempts > cfg.retryMax) {
      dropItem(item, 'retry_exhausted')
      return
    }
    const nextRetryAt = nowMs() + computeBackoff(nextAttempts)
    const updated = {
      ...item,
      attempts: nextAttempts,
      nextRetryAt,
    }
    inFlight.delete(item.id)
    if (queue.length >= cfg.maxQueueLength) {
      if (cfg.dropPolicy === 'newest') {
        dropItem(updated, 'queue_full')
        return
      }
      const dropped = queue.shift()
      if (dropped) dropItem(dropped, 'queue_full')
    }
    queue.push(updated)
    persistItem(updated)
    debug('scheduled retry', status, updated.id, nextRetryAt)
  }

  function markRetry(items, status) {
    items.forEach((item) => requeueItem(item, status))
  }

  function sendPayload(payload, { useBeacon = false, keepalive = true } = {}) {
    const body = JSON.stringify(payload)
    const url = buildBatchUrl()
    if (useBeacon && cfg.sendBeacon && navigator.sendBeacon) {
      try {
        const blob = new Blob([body], { type: 'application/json' })
        const ok = navigator.sendBeacon(url, blob)
        if (ok) return Promise.resolve({ ok: true, status: 0 })
        return Promise.resolve({ ok: false, retry: true, status: 'beacon_failed' })
      } catch (e) {
        return Promise.resolve({ ok: false, retry: true, status: 'beacon_error' })
      }
    }

    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive,
      credentials: 'omit',
    }).then((res) => {
      if (res.ok) return { ok: true, status: res.status }
      if (isRetryableStatus(res.status)) return { ok: false, retry: true, status: res.status }
      return { ok: false, retry: false, status: res.status }
    }).catch(() => ({ ok: false, retry: true, status: 'network_error' }))
  }

  function flush({ useBeacon = false, maxBytes = cfg.maxBatchBytes, reason = 'interval' } = {}) {
    if (isFlushing) return
    if (queue.length === 0) return
    if (navigator && navigator.onLine === false) {
      debug('offline, skip flush')
      return
    }
    if (microBatchHandle !== null) {
      window.clearTimeout(microBatchHandle)
      microBatchHandle = null
    }
    isFlushing = true
    const items = takeBatch(maxBytes, { allowDropLarge: !useBeacon, forBeacon: useBeacon })
    if (items.length === 0) {
      isFlushing = false
      return
    }
    const sentAt = isoNow()
    const payload = {
      batch: items.map((item) => ({ ...item.message, sentAt })),
    }
    sendPayload(payload, { useBeacon, keepalive: true }).then((result) => {
      if (result.ok) {
        markSuccess(items)
      } else if (result.retry) {
        markRetry(items, result.status)
      } else {
        markDrop(items, result.status)
      }
    }).finally(() => {
      isFlushing = false
      if (!useBeacon && queue.length > 0 && cfg.mode !== 'batch') {
        scheduleMicroBatch()
      }
      debug('flush complete', reason)
    })
  }

  function flushOnUnload() {
    if (unloadHandled) return
    unloadHandled = true
    const beaconItems = takeBatch(cfg.maxBeaconBytes, { allowDropLarge: false, forBeacon: true })
    if (beaconItems.length > 0) {
      const sentAt = isoNow()
      const payload = {
        batch: beaconItems.map((item) => ({ ...item.message, sentAt })),
      }
      sendPayload(payload, { useBeacon: true, keepalive: true }).then((result) => {
        if (result.ok) {
          markSuccess(beaconItems)
        } else if (result.retry) {
          markRetry(beaconItems, result.status)
        } else {
          markDrop(beaconItems, result.status)
        }
      })
    }
    if (queue.length > 0) {
      flush({ useBeacon: false, maxBytes: cfg.maxBatchBytes, reason: 'unload' })
    }
  }

  function bindFlushOnUnload() {
    const handler = () => {
      flushOnUnload()
    }
    window.addEventListener('pagehide', handler)
    window.addEventListener('beforeunload', handler)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') handler()
    })
  }

  function startFlushTimer() {
    if (intervalHandle !== null) return
    intervalHandle = window.setInterval(() => {
      if (queue.length > 0) flush({ reason: 'interval' })
    }, cfg.flushIntervalMs)
    window.addEventListener('online', () => {
      if (queue.length > 0) flush({ reason: 'online' })
    })
  }

  function stopFlushTimer() {
    if (intervalHandle === null) return
    window.clearInterval(intervalHandle)
    intervalHandle = null
  }

  // ---- plugin API ----
  return {
    name: 'cdp-dump-segment-compatible',
    config: cfg,

    initialize(ctx) {
      // app name is provided via plugin config
      debug('initialize ctx', ctx)
      const { appName: configAppName } = cfg
      if (configAppName) {
        appName = configAppName
      }
      bindFlushOnUnload()
      startFlushTimer()
      loadPersisted().then(() => {
        if (queue.length > 0) flush({ reason: 'startup' })
      })
      debug('initialized', { appName })
    },

    loaded() {
      return true
    },

    track(ctx) {
      const payload = ctx && ctx.payload ? ctx.payload : ctx || {}
      const msg = makeBaseMessage('track')
      msg.event = payload.event
      msg.properties = payload.properties || {}
      scheduleSend(msg)
      return msg
    },

    page(ctx) {
      // Your wrapper uses analytics.page({ name, ...props })
      const payload = ctx && ctx.payload ? ctx.payload : ctx || {}
      const msg = makeBaseMessage('page')

      // Segment page supports category + name + properties.
      // If payload looks like { name, ... }, treat all as properties.
      msg.name = payload.name || undefined
      msg.category = payload.category || undefined
      msg.properties = payload.properties || payload || {}
      scheduleSend(msg)
      return msg
    },

    identify(ctx) {
      const payload = ctx && ctx.payload ? ctx.payload : ctx || {}
      const userId = payload.userId != null ? payload.userId : payload.id
      if (userId != null) setUserId(String(userId))

      const msg = makeBaseMessage('identify')
      msg.userId = userId != null ? String(userId) : msg.userId
      msg.traits = payload.traits || {}
      scheduleSend(msg)
      return msg
    },

    group(ctx) {
      const payload = ctx && ctx.payload ? ctx.payload : ctx || {}
      const groupId = payload.groupId != null ? payload.groupId : payload.id
      if (groupId == null) return

      setGroupId(String(groupId))

      const msg = makeBaseMessage('group')
      msg.groupId = String(groupId)
      msg.traits = payload.traits || {}
      scheduleSend(msg)
      return msg
    },

    alias(ctx) {
      const payload = ctx && ctx.payload ? ctx.payload : ctx || {}
      const userId = payload.userId != null ? payload.userId : payload.id
      if (userId == null) return

      setUserId(String(userId))

      const msg = makeBaseMessage('alias')
      msg.userId = String(userId)
      if (payload.previousId != null) msg.previousId = String(payload.previousId)
      scheduleSend(msg)
      return msg
    },

    reset() {
      // Segment reset clears user identity and rotates anonymousId
      setUserId(null)
      setGroupId(null)
      removeCookie(cfg.cookieNameAnonymousId)
      ensureAnonymousId()
    },

    methods: {
      flush() {
        flush({ reason: 'manual' })
      },
    },

    onUnload() {
      flushOnUnload()
      stopFlushTimer()
    },
  }
}
